import React, { useState, useEffect, useRef, useCallback } from 'react';
import { placeService } from '../../services/placeService';

export const DELIVERY_MODES = {
  HYPER_LOCAL: 'hyper_local',
  PAN_INDIA: 'pan_india',
};

const EMPTY_ADDRESS = {
  address: '',
  pincode: '',
  latitude: '',
  longitude: '',
};

function inferDeliveryMode(addressDetails) {
  if (!addressDetails) return DELIVERY_MODES.HYPER_LOCAL;
  const hasCoords = addressDetails.latitude && addressDetails.longitude;
  const hasPincode = addressDetails.pincode;
  if (hasCoords) return DELIVERY_MODES.HYPER_LOCAL;
  if (hasPincode) return DELIVERY_MODES.PAN_INDIA;
  return DELIVERY_MODES.HYPER_LOCAL;
}

function normalizeForMode(mode, details) {
  const base = { ...EMPTY_ADDRESS, ...details };
  if (mode === DELIVERY_MODES.PAN_INDIA) {
    return {
      address: base.address,
      pincode: base.pincode,
      latitude: '',
      longitude: '',
    };
  }
  return {
    address: base.address,
    pincode: '',
    latitude: base.latitude,
    longitude: base.longitude,
  };
}

function CustomerAddressSection({ addressDetails, onChange }) {
  const [deliveryMode, setDeliveryMode] = useState(() => inferDeliveryMode(addressDetails));
  const [locationSearch, setLocationSearch] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [isSearchingPlaces, setIsSearchingPlaces] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [placeMeta, setPlaceMeta] = useState({ city: '', state: '' });
  const sessionTokenRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = crypto.randomUUID();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  const emitChange = useCallback((details, mode = deliveryMode) => {
    onChange(normalizeForMode(mode, details));
  }, [onChange, deliveryMode]);

  const handleDeliveryModeChange = (mode) => {
    setDeliveryMode(mode);
    setLocationSearch('');
    setPlaceSuggestions([]);
    setShowSuggestions(false);
    setPlaceMeta({ city: '', state: '' });
    resetSessionToken();

    if (mode === DELIVERY_MODES.PAN_INDIA) {
      emitChange(
        { address: addressDetails?.address || '', pincode: addressDetails?.pincode || '' },
        mode
      );
    } else {
      emitChange(addressDetails || EMPTY_ADDRESS, mode);
    }
  };

  const searchPlaces = useCallback(async (input) => {
    const trimmed = input.trim();
    if (trimmed.length < 3) {
      setPlaceSuggestions([]);
      setIsSearchingPlaces(false);
      return;
    }

    setIsSearchingPlaces(true);
    try {
      const token = ensureSessionToken();
      const results = await placeService.autocomplete(trimmed, token);
      setPlaceSuggestions(results);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Place autocomplete failed:', err);
      setPlaceSuggestions([]);
    } finally {
      setIsSearchingPlaces(false);
    }
  }, [ensureSessionToken]);

  const handleLocationSearchChange = (value) => {
    setLocationSearch(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.trim().length < 3) {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      setIsSearchingPlaces(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPlaces(value);
    }, 300);
  };

  const handleLocationSearchFocus = () => {
    ensureSessionToken();
    if (placeSuggestions.length > 0) setShowSuggestions(true);
  };

  const handlePlaceSelect = async (suggestion) => {
    setShowSuggestions(false);
    setLocationSearch(suggestion.description);
    setIsLoadingDetails(true);

    try {
      const token = ensureSessionToken();
      const place = await placeService.getPlaceDetails(suggestion.place_id, token);
      setPlaceMeta({ city: place.city || '', state: place.state || '' });
      emitChange({
        ...addressDetails,
        latitude: place.latitude,
        longitude: place.longitude,
        pincode: '',
      });
      resetSessionToken();
    } catch (err) {
      console.error('Place details failed:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleFieldChange = (field, value) => {
    emitChange({ ...addressDetails, [field]: value });
  };

  return (
    <div className="mt-4 space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700">Delivery area</label>
        <div className="flex flex-wrap gap-4">
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="deliveryMode"
              className="text-primary-600 focus:ring-primary-500"
              checked={deliveryMode === DELIVERY_MODES.HYPER_LOCAL}
              onChange={() => handleDeliveryModeChange(DELIVERY_MODES.HYPER_LOCAL)}
            />
            <span className="text-sm text-slate-700">Hyper Local</span>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="deliveryMode"
              className="text-primary-600 focus:ring-primary-500"
              checked={deliveryMode === DELIVERY_MODES.PAN_INDIA}
              onChange={() => handleDeliveryModeChange(DELIVERY_MODES.PAN_INDIA)}
            />
            <span className="text-sm text-slate-700">Pan India</span>
          </label>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Address</label>
        <input
          type="text"
          className="input-field"
          value={addressDetails?.address || ''}
          onChange={(e) => handleFieldChange('address', e.target.value)}
          placeholder="Enter delivery address"
        />
      </div>

      {deliveryMode === DELIVERY_MODES.HYPER_LOCAL ? (
        <>
          <div ref={containerRef} className="relative">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Search location (for coordinates)
            </label>
            <div className="relative">
              <input
                type="text"
                className="input-field pr-10"
                value={locationSearch}
                onChange={(e) => handleLocationSearchChange(e.target.value)}
                onFocus={handleLocationSearchFocus}
                placeholder="Search to fill latitude & longitude (min 3 characters)…"
              />
              {(isSearchingPlaces || isLoadingDetails) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary-600" />
                </div>
              )}
            </div>

            {showSuggestions && placeSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                {placeSuggestions.map((place) => (
                  <button
                    key={place.place_id}
                    type="button"
                    className="w-full px-4 py-2 text-left hover:bg-gray-100"
                    onClick={() => handlePlaceSelect(place)}
                  >
                    <span className="text-sm text-gray-800">{place.description}</span>
                  </button>
                ))}
              </div>
            )}

            {showSuggestions && locationSearch.trim().length >= 3 && placeSuggestions.length === 0 && !isSearchingPlaces && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500 shadow-lg">
                No places found
              </div>
            )}
          </div>

          {(placeMeta.city || placeMeta.state) && (
            <p className="text-sm text-slate-500">
              {[placeMeta.city, placeMeta.state].filter(Boolean).join(', ')}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Latitude</label>
              <input
                type="text"
                className="input-field"
                value={addressDetails?.latitude || ''}
                onChange={(e) => handleFieldChange('latitude', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Longitude</label>
              <input
                type="text"
                className="input-field"
                value={addressDetails?.longitude || ''}
                onChange={(e) => handleFieldChange('longitude', e.target.value)}
              />
            </div>
          </div>
        </>
      ) : (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Pincode</label>
          <input
            type="text"
            className="input-field"
            value={addressDetails?.pincode || ''}
            onChange={(e) => handleFieldChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit pincode"
            maxLength={6}
            inputMode="numeric"
          />
        </div>
      )}
    </div>
  );
}

export default CustomerAddressSection;
